import { FactLocationV2 } from '../../../../../backend/src/matter/fact/fact-location.entity';
import { SubsetFact } from '../../../../../backend/src/types/types';
import { FactDetails, FactDetailsLocation } from '@/api/types/backend-dto-types';
import { Chip } from '@/new/components/chip';
import { SourceConfidenceWarning } from '@/new/components/fact/source-confidence-warning';
import { Icon } from '@/new/components/icon';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';
import { ChronologyFact } from '@/types';

const CONTROL_NUMBER_FIELD = 'controlNumber';

type FactType = SubsetFact | ChronologyFact | FactDetails;

interface Props {
  fact: FactType;
  fullDocRow?: boolean;
  gotoDocReference: (docId: string, factId: string, pageNumber: number) => void;
}

const isChronologyFact = (fact: FactType): fact is ChronologyFact => {
  return 'fact' in fact && 'chronologyId' in fact;
};

export const FactDocumentDetails = ({ fact, fullDocRow = true, gotoDocReference }: Props) => {
  const uploadDate = new Date(fact.createdDate);

  // Handle SubsetFact, ChronologyFact, and FactDetailsResponseDto
  const locations = isChronologyFact(fact) ? fact.fact.locations : fact.locations;
  const factId = isChronologyFact(fact) ? fact.fact.id : fact.id;

  return (
    <div className="flex flex-col gap-2">
      <p className="font-medium">Sources</p>

      {locations.map((factLocation: FactLocationV2 | FactDetailsLocation, index: number) => {
        const pageNumber = factLocation?.boundingBoxes?.length > 0 ? factLocation?.boundingBoxes?.[0]?.pageNumber : 1;
        const fileMetadata = factLocation.doc.file.fileMetadata as Record<string, unknown> | null | undefined;
        const relativityFields = fileMetadata?.relativityFields as Record<string, unknown> | null | undefined;
        const hasControlNumber = relativityFields && CONTROL_NUMBER_FIELD in relativityFields;

        return (
          <div key={`${factLocation.docId}-${index}`} className="flex w-full flex-col gap-2 not-last:mb-2">
            <div className="flex w-full items-baseline gap-2">
              <div className="flex items-center gap-2">
                <p
                  onClick={() => gotoDocReference(factLocation.docId, factId, pageNumber)}
                  className="flex cursor-pointer items-center gap-1.25 text-xs break-all text-ellipsis text-blue-600 duration-150 hover:text-blue-700"
                >
                  <Icon className="lucide--file mb-0.25" />
                  <span className="line-clamp-1">{factLocation.doc?.file.name}</span>
                </p>

                {hasControlNumber && (
                  <Chip className={cn(CHIP_COLOURS.relativity)}>
                    <Icon className="lucide--database" />
                    {relativityFields?.[CONTROL_NUMBER_FIELD] as string}
                  </Chip>
                )}

                <Chip className={CHIP_COLOURS.type}>{factLocation.doc?.type}</Chip>
              </div>

              {fullDocRow && (
                <div className="ml-auto text-xs whitespace-nowrap text-gray-500">
                  Uploaded{' '}
                  <span className="font-semibold">{`${uploadDate.getDate()}/${uploadDate.getMonth() + 1}/${uploadDate.getFullYear()}`}</span>
                </div>
              )}
            </div>

            <div className="border-l-2 border-gray-200 pl-4 text-gray-600">
              {factLocation.sources &&
                factLocation?.sources?.map((f, i) => {
                  return (
                    <div key={i} className="py-1 text-xs">
                      <div className="mb-0.75 flex items-center gap-2">
                        <span>
                          [Page{f.pages.includes(',') ? 's' : ''} {f.pages}]
                        </span>
                        <SourceConfidenceWarning sourceConfidence={f.sourceConfidence} />
                      </div>
                      {f.text && f.text.length > 900 ? f.text.slice(0, 900) + '...' : f.text}
                    </div>
                  );
                })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
